import { get, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import { DEFAULT_SPACING_SIZE } from 'hb-builder/components/BuilderComponents/constants/spacingSize';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import type {
  OnChangeSettingsArgs,
  OnChangeSetting,
} from 'hb-builder/types/settings';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes';
import {
  BUTTON_MODE_OPTIONS,
  BUTTON_VARIANT_OPTIONS,
} from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';

export interface StylingModeProps {
  defaultValue: BUTTON_MODE_OPTIONS;
  item: DocumentItem<ItemAttributes>;
  onChange: OnChangeSetting;
  settingKey?: string;
  setStylingMode: (value: BUTTON_MODE_OPTIONS) => void;
  title?: string;
}

export const STYLING_MODE_DEFAULT_VALUE = BUTTON_MODE_OPTIONS.NETWORK;

export const options = [
  { label: s__('Builder|Network theme'), value: BUTTON_MODE_OPTIONS.NETWORK },
  { label: s__('Builder|Custom'), value: BUTTON_MODE_OPTIONS.CUSTOM },
];

const getPaddingInPxFromSize = ({ size }) => {
  switch (size) {
    case 'small':
      return '4px 12px';
    case 'normal':
      return '8px 12px';
    case 'large':
      return '10px 16px';
    default:
      return '8px 12px';
  }
};

export const buildButtonCustomAttributesFromNetworkTheme = ({
  item,
  buttonTheme,
  colors,
}) => [
  {
    settingKey: 'attributes.buttonBackgroundColor',
    value:
      item.attributes.buttonBackgroundColor ||
      colors[buttonTheme.bg] ||
      buttonTheme.bg,
  },
  {
    settingKey: 'attributes.buttonColor',
    value:
      item.attributes.buttonColor ||
      colors[buttonTheme?.color] ||
      buttonTheme.color,
  },
  {
    settingKey: 'attributes.buttonFontFamily',
    value: item.attributes.buttonFontFamily,
  },
  {
    settingKey: 'attributes.buttonFontSize',
    value:
      item.attributes.buttonFontSize || convertRemToPx(buttonTheme.fontSize),
  },
  {
    settingKey: 'attributes.buttonLetterSpacing',
    value: item.attributes.buttonLetterSpacing || 0,
  },
  {
    settingKey: 'attributes.buttonPadding',
    value:
      item.attributes.buttonPadding ||
      getPaddingInPxFromSize({ size: item.attributes.size }),
  },
  {
    settingKey: 'attributes.buttonRadius',
    value: item.attributes.buttonRadius || buttonTheme.borderRadius,
  },
  {
    settingKey: 'attributes.borderColor',
    value: item.attributes.borderColor || '#000000',
  },
  {
    settingKey: 'attributes.borderWidth',
    value: item.attributes.borderWidth || 3,
  },
  {
    settingKey: 'attributes.hasBorder',
    value: item.attributes.hasBorder || false,
  },
  {
    settingKey: 'attributes.mode',
    value: BUTTON_MODE_OPTIONS.CUSTOM,
  },
];

// This setting can be applied in bulk or individually.
const StylingMode = ({
  defaultValue,
  item: initialItem,
  onChange,
  settingKey = 'mode',
  setStylingMode,
  title = s__('Builder|Styling mode'),
}: StylingModeProps) => {
  // We need to retrieve the FO/PO theme values.
  const { componentTheme } = useBuilderConfigContext();

  const isBulk = !isEmpty(initialItem.children);
  const referenceItem = isBulk ? initialItem.children?.[0] : initialItem;
  const items = isBulk ? initialItem.children : [initialItem];

  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(get(referenceItem?.attributes, settingKey));
  }, [referenceItem?.attributes, settingKey]);

  const handleChangeStylingMode = ({ value }: OnChangeSettingsArgs) => {
    setStylingMode(value);

    // We only want to reset the childrenSpacing attributes if it initially exists (only for Buttons, not Button components)
    if (initialItem.attributes?.childrenSpacing) {
      onChange({
        id: initialItem.id,
        settingKey: 'attributes.childrenSpacing',
        value: DEFAULT_SPACING_SIZE,
      });
    }

    if (items) {
      items.forEach((button) => {
        if (value === BUTTON_MODE_OPTIONS.CUSTOM) {
          onChange({
            id: button.id,
            values: buildButtonCustomAttributesFromNetworkTheme({
              item: button,
              buttonTheme:
                componentTheme.buttons[
                  button.attributes?.variant || BUTTON_VARIANT_OPTIONS.PRIMARY
                ],
              colors: componentTheme.colors,
            }),
          });
        } else {
          onChange({
            id: button.id,
            settingKey: 'attributes.mode',
            value: BUTTON_MODE_OPTIONS.NETWORK,
          });
        }
      });
    }
  };

  const handleChange = (e) => {
    setValue(e.value);
    handleChangeStylingMode({ value: e.value });
  };

  const formattedValue = getReactSelectValue(options, value);

  return (
    <Wrapper data-testid="setting-styling-mode">
      <InputTitle id="settingsStylingMode" label={title} position="left" />
      <ReactSelectInput
        id="settingsStylingMode"
        name="settingsStylingMode"
        onChange={handleChange}
        options={options}
        placeholder={value}
        value={formattedValue}
        variant="small"
        width={155}
      />
    </Wrapper>
  );
};

export default StylingMode;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
  mt: '6px',
}))``;
